import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  User,
  Mail,
  Phone,
  Calendar as CalendarIcon,
  Clock,
  Plus,
  CreditCard,
  ChevronRight,
  Trash2,
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  Bell,
  Link2,
  Loader2,
} from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../../styles/datepicker-custom.css";
import TimeSpinner from "../ui/TimeSpinner";
import {
  requestCancelBooking,
  updateSingleBookingStatus,
  updateBookingDate,
  updateBooking,
  createDepositPaymentLink,
  sendBookingConfirmation,
  sendBookingReminder,
} from "../../services/bookings";
import { useAuth } from "../../auth/AuthContext";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectValue,
  SelectItem,
} from "../../components/ui/select";
import { Customer } from "../../types/customer";
import GlobalLoader from "../ui/GlobalLoader";
import { useToast } from "../../hooks/useToast";
import { cn } from "../../lib/utils";
import BackButton from "./BackButton";
import { getForms } from "../../services/forms";
import { FormTemplate } from "../../types/forms";

import { Service } from "../../types/calendar";
import { SelectedProduct } from "./ProductSelectionStep";
import { ShoppingBag } from "lucide-react";
import DepositLinkPopup from "../modals/DepositLinkPopup";

interface Props {
  selectedServices: Service[];
  selectedCustomer: Customer;
  bookingData?: any | null;
  setStep: (
    step:
      | "selection"
      | "customer"
      | "summary"
      | "payment"
      | "finalPayment"
      | "posPayment",
  ) => void;
  appointmentTime: string;
  tip: number;
  setTip: (tip: number) => void;
  onRebook: () => void;
  setIsAddingService: (value: boolean) => void;
  setSelectionMode?: (mode: "Services" | "Sunbeds") => void;
  selectionMode?: "Services" | "Sunbeds";
  stylist?: string;
  staffList?: { id: number; name: string; color: string }[];
  onSaveAsBooked?: () => Promise<void>;
  allTaxes?: any[];
  onRefresh?: () => void;
  setSelectedServices?:
  | ((services: Service[]) => void)
  | React.Dispatch<React.SetStateAction<Service[]>>;
  onClose?: () => void;
  rebookMode?: boolean;
  selectedDateTime?: Date;
  setSelectedDateTime?: (date: Date) => void;
  onStylistChange?: (
    stylist: string,
    date?: string,
    time?: string,
  ) => Promise<void>;
  isAvailable?: boolean | null;
  isCheckingAvailability?: boolean;
  recommendedSlots?: string[];
  consultationFormIds?: number[];
  onConsultationFormsChange?: (ids: number[]) => void;
  /** Products added to this booking */
  selectedProducts?: SelectedProduct[];
  /** Opens the product selection step */
  onAddProducts?: () => void;
  /** Removes a single product by id */
  onRemoveProduct?: (id: string | number) => void;
}

const SummaryStep: React.FC<Props> = ({
  selectedServices,
  selectedCustomer,
  bookingData,
  setStep,
  onRebook,
  setIsAddingService,
  setSelectionMode,
  selectionMode,
  appointmentTime,
  stylist,
  staffList,
  onSaveAsBooked,
  allTaxes,
  onRefresh,
  setSelectedServices,
  onClose,
  rebookMode = false,
  selectedDateTime: propSelectedDateTime,
  setSelectedDateTime: propSetSelectedDateTime,
  onStylistChange,
  isAvailable,
  isCheckingAvailability,
  recommendedSlots,
  consultationFormIds = [],
  onConsultationFormsChange,
  selectedProducts = [],
  onAddProducts,
  onRemoveProduct,
}) => {
  const { user } = useAuth();
  const currencySymbol =
    user?.company?.currency_symbol ||
    user?.company?.currency?.currency_symbol ||
    "";
  const toast = useToast();
  const [status, setStatus] = useState<string>(bookingData?.status || "booked");
  const [date, setDate] = useState<string>(
    bookingData?.date?.split(/[ T]/)[0] || "",
  );
  const [loading, setLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState<
    "confirm" | "reminder" | "deposit" | null
  >(null);
  const [depositLink, setDepositLink] = useState<string | null>(null);
  const [depositAmount, setDepositAmount] = useState<number | null>(null);
  const [depositCurrency, setDepositCurrency] = useState<string | null>(null);
  const [showDepositPopup, setShowDepositPopup] = useState(false);

  const paymentStatus = String(
    bookingData?.payment_status ??
      bookingData?.rawBooking?.payment_status ??
      "",
  ).toLowerCase();
  const pendingAmount = Number(
    bookingData?.pending_amount ??
      bookingData?.rawBooking?.pending_amount ??
      bookingData?.rawBooking?.total_pending ??
      1,
  );
  const bookingStatus = String(bookingData?.status || "").toLowerCase();
  const canRequestDeposit =
    !!bookingData &&
    !rebookMode &&
    paymentStatus !== "completed" &&
    pendingAmount > 0 &&
    bookingStatus !== "completed" &&
    bookingStatus !== "cancelled";

  const handleSendConfirmation = async () => {
    if (!bookingData?.id || actionBusy) return;
    setActionBusy("confirm");
    try {
      const res = await sendBookingConfirmation(bookingData.id);
      toast.success(res?.message || "Confirmation sent.");
    } catch (error: any) {
      toast.error(error?.message || "Failed to send confirmation.");
    } finally {
      setActionBusy(null);
    }
  };

  const handleSendReminder = async () => {
    if (!bookingData?.id || actionBusy) return;
    setActionBusy("reminder");
    try {
      const res = await sendBookingReminder(bookingData.id);
      toast.success(res?.message || "Reminder sent.");
    } catch (error: any) {
      toast.error(error?.message || "Failed to send reminder.");
    } finally {
      setActionBusy(null);
    }
  };

  const handleRequestDeposit = async () => {
    if (!bookingData?.id || actionBusy) return;
    setActionBusy("deposit");
    try {
      const res = await createDepositPaymentLink(bookingData.id, true);
      const url = res?.short_url || res?.payment_url || null;
      setDepositLink(url);
      setDepositAmount(
        res?.amount != null ? Number(res.amount) : null,
      );
      setDepositCurrency(res?.currency || null);
      setShowDepositPopup(!!url);
      toast.success(res?.message || "Deposit payment link sent.");
      if (url && navigator?.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(url);
        } catch {
          /* ignore */
        }
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to create deposit payment link.");
    } finally {
      setActionBusy(null);
    }
  };

  useEffect(() => {
    setDepositLink(null);
    setDepositAmount(null);
    setDepositCurrency(null);
    setShowDepositPopup(false);
  }, [bookingData?.id]);

  // ================================
  // CONSULTATION FORMS STATE
  // ================================

  const [allForms, setAllForms] = useState<FormTemplate[]>([]);
  const [serviceForms, setServiceForms] = useState<FormTemplate[]>([]);
  const [extraForms, setExtraForms] = useState<FormTemplate[]>([]);
  const lastEmittedIdsRef = useRef<number[]>([]);

  // All forms that will be sent
  const allSelectedForms = [...serviceForms, ...extraForms];
  const selectedConsultationFormIds = allSelectedForms.map((f) => Number(f.id));

  // Forms available to add in dropdown (exclude already selected)
  const availableOtherForms = allForms.filter(
    (f) => !allSelectedForms.some((sf) => sf.id === f.id),
  );

  const handleSave = async () => {
    const isActuallyRebooking = rebookMode || !bookingData;

    if (!bookingData && !isActuallyRebooking) return;

    setLoading(true);
    try {
      const currentStylist = staffList?.find((s) => s.name === stylist);
      const employeeId = currentStylist?.id || staffList?.[0]?.id || 1;

      // Map selected services to the format backend expects
      const item_ids = selectedServices.map((s) =>
        String(s.id).replace("service-", "").replace("sunbed-", ""),
      );
      const types = selectedServices.map((s) =>
        String(s.id).includes("sunbed") ? "sunbed" : "service",
      );
      const cart_quantity = selectedServices.map(() => 1);
      const item_prices = selectedServices.map((s) => {
        const priceStr =
          typeof s.price === "string" ? s.price.replace(/[£$€]/g, "") : s.price;
        return Number(priceStr || 0);
      });

      const finalDate = propSelectedDateTime
        ? propSelectedDateTime.toLocaleDateString("en-CA")
        : date;
      const finalTime = propSelectedDateTime
        ? propSelectedDateTime.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        })
        : appointmentTime;

      const payload = {
        booking_date: finalDate,
        hidden_booking_time: finalTime,
        status: status,
        employee_id: [employeeId],
        item_ids,
        types,
        cart_quantity,
        item_prices,
        paid_amount: totalPrice + totalTax,
        payment_status: status === "completed" ? "completed" : "pending",
        user_id: selectedCustomer.id,
        location: bookingData?.locationId || 1, // Defaulting to 1 if not present
        consultation_form_ids: selectedConsultationFormIds,
      };

      if (rebookMode || !bookingData) {
        // Create new booking (Rebook)
        await onSaveAsBooked?.();
      } else {
        // Update existing booking
        await updateBooking(bookingData.id, payload);
      }

      toast.success(
        rebookMode ? "Rebooked successfully!" : "Booking updated successfully!",
      );
      if (onRefresh) onRefresh();
      if (onClose) onClose();
    } catch (err: any) {
      console.error("Error updating/rebooking:", err);
      const errorMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error?.message ||
        err?.message ||
        "Failed to process booking.";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const calculateServicePrice = (s: Service) => {
    const originalPrice =
      typeof s.price === "string"
        ? parseFloat(s.price.replace(/[£$€]/g, ""))
        : Number(s.price || 0);
    const discount = parseFloat(String(s.discount)) || 0;
    const discountType = s.discount_type;

    if (discount > 0) {
      let discountedPrice = originalPrice;
      if (discountType === "percent") {
        discountedPrice = originalPrice - (originalPrice * discount) / 100;
      } else {
        // "fixed" or "amount"
        discountedPrice = originalPrice - discount;
      }
      return Math.max(0, discountedPrice);
    }
    return originalPrice;
  };

  const calculateServiceTax = (s: Service) => {
    const price = calculateServicePrice(s);

    // 1. Try using embedded tax objects (service_taxes, tax_services or taxes)
    const embeddedTaxes = s.service_taxes || s.tax_services || (s as any).taxes;
    if (
      embeddedTaxes &&
      Array.isArray(embeddedTaxes) &&
      embeddedTaxes.length > 0
    ) {
      const totalTaxPercent = embeddedTaxes.reduce((sum, item: any) => {
        const percent =
          item.tax?.percent ??
          item.percent ??
          item.tax?.percent_percent ??
          item.percent_percent ??
          0;
        return sum + Number(percent);
      }, 0);
      return (price * totalTaxPercent) / 100;
    }

    if (!allTaxes) return 0;

    let applicableTaxIds: number[] = [];

    // Check tax_ids (common in mutations/some API responses)
    if (s.tax_ids && Array.isArray(s.tax_ids)) {
      applicableTaxIds = s.tax_ids.map((id) => Number(id));
    }
    // Fallback: Check taxes (common in BusinessService model)
    else if ((s as any).taxes && Array.isArray((s as any).taxes)) {
      applicableTaxIds = (s as any).taxes.map((id: any) => Number(id));
    }

    const totalTaxPercent = allTaxes
      .filter((t) => applicableTaxIds.includes(Number(t.id)))
      .reduce((sum, t) => sum + (t.percent || 0), 0);

    return (price * totalTaxPercent) / 100;
  };

  const totalTaxRaw = selectedServices.reduce((sum, s) => {
    if (!s) return sum;
    return sum + calculateServiceTax(s);
  }, 0);

  // If detailed tax calculation yields 0, fallback to the total tax stored on the booking (if any)
  const totalTax =
    totalTaxRaw === 0 && bookingData
      ? Number(
        (bookingData as any).tax_amount ||
        (bookingData as any).total_tax ||
        0,
      )
      : totalTaxRaw;

  // Products subtotal (catalog price before any product-level discount)
  const productsSubtotal = (selectedProducts ?? []).reduce((sum, p) => {
    return sum + Number(p.price || 0) * p.quantity;
  }, 0);

  // Products total after discount
  const productsTotal = (selectedProducts ?? []).reduce((sum, p) => {
    const effectivePrice = p.discountedPrice ?? p.price;
    return sum + Number(effectivePrice || 0) * p.quantity;
  }, 0);

  const totalPrice = selectedServices.reduce((sum, s) => {
    if (!s) return sum;
    return sum + calculateServicePrice(s);
  }, 0) + productsTotal;

  const subtotal = selectedServices.reduce((sum, s) => {
    if (!s) return sum;
    return (
      sum +
      (typeof s.price === "string"
        ? parseFloat(s.price.replace(/[£$€]/g, ""))
        : Number(s.price || 0))
    );
  }, 0) + productsSubtotal;

  const totalDiscount = subtotal - totalPrice;

  const getInitials = (name: string) => {
    if (!name) return "??";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };
  useEffect(() => {
    const loadForms = async () => {
      try {
        const forms = await getForms();
        setAllForms(forms);
      } catch (err) {
        console.error("Failed to load consultation forms", err);
      }
    };

    loadForms();
  }, []);

  // 1. INITIALIZATION & SYNC: Ensure local state matches parent IDs (especially for Edit mode)
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    if (!allForms.length) return;

    const parentIds = consultationFormIds || [];

    // Initialize local state when entering the step
    if (!hasInitializedRef.current) {
      // 1. Get all forms linked to the currently selected services
      const linked = allForms.filter((form) =>
        form.services?.some((svc) =>
          selectedServices.some(
            (s) => String(s.id).replace(/\D/g, "") === String(svc.id),
          ),
        ),
      );

      // 2. Get any extra forms that were already present in parentIds (e.g. from existing booking)
      const extras = allForms.filter(
        (f) =>
          parentIds.includes(Number(f.id)) && !linked.some((l) => l.id === f.id),
      );

      setServiceForms(linked);
      setExtraForms(extras);
      hasInitializedRef.current = true;
    }
  }, [allForms, consultationFormIds, selectedServices, bookingData, rebookMode]);

  // 2. EMIT: Send changes back to parent
  useEffect(() => {
    if (!onConsultationFormsChange || !hasInitializedRef.current) return;

    const ids = [...serviceForms, ...extraForms].map((f) => Number(f.id));

    // Compare with last emitted AND parent prop to avoid feedback loops
    const parentIds = consultationFormIds || [];
    const isSameAsParent = ids.length === parentIds.length && ids.every(id => parentIds.includes(Number(id)));
    const isSameAsLast = ids.length === lastEmittedIdsRef.current.length && ids.every((id, i) => id === ids[i]);

    if (isSameAsParent || isSameAsLast) return;


    lastEmittedIdsRef.current = ids;
    onConsultationFormsChange(ids);
  }, [serviceForms, extraForms, onConsultationFormsChange, consultationFormIds]);

  return (
    <div className="absolute inset-0 flex flex-col bg-white overflow-hidden">
      <div className="flex-1 flex flex-col lg:flex-row overflow-y-auto lg:overflow-hidden custom-scrollbar">
        {/* LEFT PANEL - Customer Profile & Actions */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="w-full lg:w-[35%] bg-white lg:border-r border-gray-100 flex flex-col flex-shrink-0"
        >
          <div className="lg:flex-1 lg:overflow-y-auto p-6 sm:p-8 custom-scrollbar">
            <div className="flex flex-col items-center text-center space-y-4 mb-10">
              <div className="w-24 h-24 rounded-full bg-[#885ABB]/10 flex items-center justify-center text-[#885ABB] text-3xl font-bold shadow-sm border-4 border-white">
                {getInitials(selectedCustomer.name)}
              </div>
              <div>
                <h3 className="text-xl font-[600] text-[#402B69] mb-1">
                  {selectedCustomer.name}
                </h3>
                <p className="text-sm text-gray-400 font-medium">
                  {selectedCustomer.email || "No email provided"}
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="space-y-3">
                <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest px-1">
                  Contact Info
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 text-sm text-gray-500 font-medium">
                    <Phone size={14} />{" "}
                    <span>{selectedCustomer.phone || "Not provided"}</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 text-sm text-gray-500 font-medium">
                    <Mail size={14} />{" "}
                    <span>{selectedCustomer.email || "Not provided"}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest px-1">
                  Appointment Details
                </h4>
                <div className="space-y-3">
                  {rebookMode ? (
                    <>
                      {/* Date & Time Picker */}
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-1">
                          Date & Time
                        </label>
                        <div className="flex flex-col gap-2">
                          <DatePicker
                            selected={propSelectedDateTime || new Date()}
                            onChange={(date: Date | null) => {
                              if (date && propSetSelectedDateTime) {
                                propSetSelectedDateTime(date);
                                if (onStylistChange && stylist) {
                                  onStylistChange(
                                    stylist,
                                    date.toLocaleDateString("en-CA"),
                                    date.toLocaleTimeString("en-US", {
                                      hour: "numeric",
                                      minute: "2-digit",
                                      hour12: true,
                                    }),
                                  );
                                }
                              }
                            }}
                            showTimeInput
                            customTimeInput={<TimeSpinner />}
                            dateFormat="dd MMM yyyy, h:mm aa"
                            className="h-11 w-full border border-gray-100 bg-gray-50 rounded-xl text-sm font-semibold text-[#402B69] px-4 shadow-sm hover:border-[#885ABB] transition-all focus:outline-none"
                            calendarClassName="custom-datepicker"
                            popperPlacement="bottom-end"
                            portalId="root"
                            renderCustomHeader={({
                              date,
                              decreaseMonth,
                              increaseMonth,
                            }) => (
                              <div className="flex items-center justify-between px-3 py-1 bg-[#885ABB] rounded-t-lg">
                                <button
                                  onClick={decreaseMonth}
                                  type="button"
                                  className="p-1 text-white hover:bg-white/20 rounded-lg"
                                >
                                  <ChevronLeft size={18} />
                                </button>
                                <span className="text-white font-semibold text-xs uppercase">
                                  {date.toLocaleString("default", {
                                    month: "long",
                                    year: "numeric",
                                  })}
                                </span>
                                <button
                                  onClick={increaseMonth}
                                  type="button"
                                  className="p-1 text-white hover:bg-white/20 rounded-lg"
                                >
                                  <ChevronRight size={18} />
                                </button>
                              </div>
                            )}
                          />
                        </div>
                      </div>

                      {/* Stylist Picker */}
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-1">
                          Stylist
                        </label>
                        <Select
                          value={stylist}
                          onValueChange={(newStylist) => {
                            if (onStylistChange) {
                              onStylistChange(
                                newStylist,
                                propSelectedDateTime?.toLocaleDateString(
                                  "en-CA",
                                ),
                                propSelectedDateTime?.toLocaleTimeString(
                                  "en-US",
                                  {
                                    hour: "numeric",
                                    minute: "2-digit",
                                    hour12: true,
                                  },
                                ),
                              );
                            }
                          }}
                        >
                          <SelectTrigger className="h-11 border border-gray-100 rounded-xl text-sm font-semibold text-[#402B69] bg-gray-50 focus:ring-0">
                            <SelectValue placeholder="Stylist" />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-gray-100 rounded-xl z-[10001]">
                            {staffList?.map((s) => (
                              <SelectItem key={s.id} value={s.name}>
                                {s.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        {/* Availability Indicator */}
                        {rebookMode && (
                          <div className="mt-2 min-h-[20px]">
                            {isCheckingAvailability ? (
                              <div className="flex items-center gap-2 text-[10px] text-gray-400 font-medium">
                                <div className="w-2 h-2 border-2 border-[#885ABB] border-t-transparent rounded-full animate-spin" />
                                Checking availability...
                              </div>
                            ) : isAvailable === true ? (
                              <div className="flex items-center gap-2 text-[10px] text-green-500 font-bold">
                                <CheckCircle2 size={12} />
                                Stylist is available for this time
                              </div>
                            ) : isAvailable === false ? (
                              <div className="flex items-center gap-2 text-[10px] text-red-500 font-bold">
                                <AlertCircle size={12} />
                                Stylist is NOT available
                              </div>
                            ) : null}
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center justify-between p-3 rounded-xl bg-[#885ABB]/5 text-sm text-[#885ABB] font-bold">
                      <div className="flex items-center gap-3">
                        <Clock size={14} /> <span>{appointmentTime}</span>
                      </div>
                      <span className="text-[10px] uppercase">Slot</span>
                    </div>
                  )}
                </div>
              </div>

              {bookingData && !rebookMode && (
                <div className="pt-4 space-y-3">
                  <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest px-1">
                    Manage Status
                  </h4>
                  {bookingData.status === "completed" ? (
                    <div className="flex items-center justify-between p-3 rounded-xl bg-green-50 text-sm text-green-700 font-bold border border-green-100">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 size={14} />
                        <span className="capitalize">{status}</span>
                      </div>
                      <span className="text-[10px] uppercase">Final</span>
                    </div>
                  ) : (
                    <Select value={status} onValueChange={setStatus}>
                      <SelectTrigger className="h-11 border border-gray-100 rounded-xl text-sm font-semibold text-[#402B69] bg-gray-50 focus:ring-0">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-100 rounded-xl z-[10001]">
                        <SelectItem value="booked">Booked</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="no_show">No Show</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  )}

                  {bookingStatus !== "completed" &&
                    bookingStatus !== "cancelled" && (
                      <div className="flex flex-wrap gap-2 pt-1">
                        <button
                          type="button"
                          onClick={handleSendConfirmation}
                          disabled={!!actionBusy}
                          className="flex items-center gap-1.5 px-3 py-2 bg-[#885ABB]/10 text-[#885ABB] rounded-xl text-xs font-bold hover:bg-[#885ABB] hover:text-white transition-all disabled:opacity-50"
                        >
                          {actionBusy === "confirm" ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <Mail size={14} />
                          )}
                          <span>Confirm</span>
                        </button>
                        <button
                          type="button"
                          onClick={handleSendReminder}
                          disabled={!!actionBusy}
                          className="flex items-center gap-1.5 px-3 py-2 bg-[#885ABB]/10 text-[#885ABB] rounded-xl text-xs font-bold hover:bg-[#885ABB] hover:text-white transition-all disabled:opacity-50"
                        >
                          {actionBusy === "reminder" ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <Bell size={14} />
                          )}
                          <span>Reminder</span>
                        </button>
                        {canRequestDeposit && (
                          <button
                            type="button"
                            onClick={handleRequestDeposit}
                            disabled={!!actionBusy}
                            className="flex items-center gap-1.5 px-3 py-2 bg-emerald-50 text-emerald-700 rounded-xl text-xs font-bold hover:bg-emerald-600 hover:text-white transition-all disabled:opacity-50"
                          >
                            {actionBusy === "deposit" ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <Link2 size={14} />
                            )}
                            <span>Request Deposit</span>
                          </button>
                        )}
                      </div>
                    )}

                </div>
              )}
              {/* ============================= */}
              {/* CONSULTATION FORMS SECTION */}
              {/* TODO (BACKEND): Wire this with real service → forms APIs */}
              {/* ============================= */}
              <div className="pt-6 space-y-4">
                <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest px-1">
                  Consultation Forms
                </h4>

                {/* FORMS THAT WILL BE SENT */}
                <div className="space-y-2">
                  <p className="text-[10px] text-gray-400 font-semibold px-1">
                    Forms attached
                  </p>

                  {allSelectedForms.length === 0 ? (
                    <p className="text-[11px] text-gray-400 italic px-1">
                      No consultation forms attached
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {allSelectedForms.map((form) => (
                        <div
                          key={form.id}
                          className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#885ABB]/10 text-[#885ABB] text-xs font-bold"
                        >
                          <span className="truncate max-w-[120px]">
                            {form.title}
                          </span>

                          {bookingData?.status !== "completed" &&
                            bookingData?.status !== "Completed" &&
                            !rebookMode && (
                              <button
                                onClick={() => {
                                  setServiceForms((prev) =>
                                    prev.filter((f) => f.id !== form.id),
                                  );
                                  setExtraForms((prev) =>
                                    prev.filter((f) => f.id !== form.id),
                                  );
                                }}
                                className="hover:text-red-500 transition-colors"
                                title="Remove form"
                              >
                                ✕
                              </button>
                            )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* ADD OTHER FORMS */}
                {bookingData?.status !== "completed" &&
                  bookingData?.status !== "Completed" &&
                  !rebookMode &&
                  availableOtherForms.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-[10px] text-gray-400 font-semibold px-1">
                        Add another form
                      </p>

                      <Select
                        onValueChange={(value) => {
                          const selected = availableOtherForms.find(
                            (f) => String(f.id) === value,
                          );
                          if (selected) {
                            setExtraForms((prev) => [...prev, selected]);
                          }
                        }}
                      >
                        <SelectTrigger className="h-10 border border-gray-100 rounded-xl text-xs font-semibold text-[#402B69] bg-gray-50 focus:ring-0">
                          <SelectValue placeholder="Select consultation form" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-100 rounded-xl z-[10001]">
                          {availableOtherForms.map((form) => (
                            <SelectItem key={form.id} value={String(form.id)}>
                              {form.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
              </div>
            </div>
          </div>

          {!bookingData && (
            <div className="px-6 sm:px-8 pb-8 sm:pb-6 pt-2 sticky bottom-0 bg-white lg:static border-t lg:border-t-0 border-gray-50 z-20">
              <button
                onClick={() => setStep("customer")}
                className="w-full h-12 bg-white border border-gray-200 text-[#402B69] rounded-xl font-bold hover:bg-gray-50 transition-all text-sm flex items-center justify-center gap-2 shadow-sm"
              >
                <User size={16} />
                Change Customer
              </button>
            </div>
          )}
        </motion.div>

        {/* RIGHT PANEL - Services & Summary */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex-1 bg-[#F9F9F9] flex flex-col relative lg:overflow-hidden"
        >
          {/* Internal Header for Right Panel */}
          <div className="px-4 lg:px-6 pt-4 lg:pt-8 pb-3 lg:pb-5 bg-white border-b border-gray-100 shadow-sm relative z-20">
            <div className="flex items-center justify-between mb-3 lg:mb-4">
              <div>
                <h2 className="text-lg lg:text-[22px] font-bold text-[#402B69] leading-tight">
                  {bookingData?.status === "completed" ||
                    bookingData?.status === "Completed"
                    ? "Appointment Details"
                    : selectionMode === "Sunbeds"
                      ? "Add Sunbed Session"
                      : "Add Appointment"}
                </h2>
                <div className="flex items-center gap-2 mt-0.5 lg:mt-1">
                  <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                    {bookingData?.status === "completed" ||
                      bookingData?.status === "Completed"
                      ? "Appointment Info"
                      : "Step 3 of 4: Summary"}
                  </span>
                </div>
              </div>
              {bookingData?.status === "completed" ||
                bookingData?.status === "Completed" ? null : (
                <BackButton onClick={() => setStep("customer")} />
              )}
            </div>

            {bookingData?.status === "completed" ||
              bookingData?.status === "Completed" ? null : (
              <div className="flex items-center gap-1">
                {[0, 1, 2, 3].map((s) => (
                  <div
                    key={s}
                    className={cn(
                      "h-1 lg:h-1.5 flex-1 rounded-full transition-all duration-500",
                      s <= 2 ? "bg-[#885ABB]" : "bg-gray-100",
                    )}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="flex-1 lg:overflow-y-auto custom-scrollbar">
            <div className="space-y-6 p-4 sm:p-6 pb-2">
              <section className="space-y-4">
                <div className="flex justify-between items-center px-1">
                  <h4 className="text-[11px] font-[600] text-[#402B69] uppercase tracking-wider opacity-60">
                    Services
                  </h4>
                  <span className="text-[10px] font-bold text-[#885ABB] bg-white border border-[#885ABB]/20 px-2.5 py-1 rounded-full">
                    {selectedServices.length} Selected
                  </span>
                </div>

                <div className="space-y-2">
                  {selectedServices.filter(Boolean).map((service, idx) => (
                    <div
                      key={service.id + idx}
                      className="bg-white rounded-xl p-4 border border-gray-100 flex flex-col gap-3 shadow-sm"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-1.5 h-8 bg-[#885ABB] rounded-full" />
                          <div className="flex-1 min-w-0">
                            <p className="text-[14px] font-[600] text-[#402B69] truncate uppercase tracking-tight">
                              {service.name}
                            </p>
                            <div className="flex items-center gap-2 text-[10px] text-gray-400 font-medium mt-0.5">
                              <Clock size={10} />{" "}
                              <span>{service.duration}</span>
                              <span className="w-1 h-1 rounded-full bg-gray-300 mx-0.5" />
                              <User size={10} />{" "}
                              <span className="truncate">
                                {service.assignedStylistName || stylist || "Not assigned"}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex flex-col items-end">
                            {(() => {
                              const originalPrice =
                                typeof service.price === "string"
                                  ? parseFloat(
                                    service.price.replace(/[£$€]/g, ""),
                                  )
                                  : Number(service.price || 0);
                              const discountedPrice =
                                calculateServicePrice(service);

                              if (discountedPrice < originalPrice) {
                                return (
                                  <>
                                    <span className="text-[10px] font-bold text-gray-400 line-through">
                                      {currencySymbol}
                                      {originalPrice.toFixed(2)}
                                    </span>
                                    <span className="text-base font-bold text-[#885ABB] whitespace-nowrap">
                                      {currencySymbol}
                                      {discountedPrice.toFixed(2)}
                                    </span>
                                  </>
                                );
                              }

                              return (
                                <span className="text-base font-bold text-[#885ABB] whitespace-nowrap">
                                  {currencySymbol}
                                  {originalPrice.toFixed(2)}
                                </span>
                              );
                            })()}
                          </div>
                          {setSelectedServices &&
                            !rebookMode &&
                            bookingData?.status !== "completed" &&
                            bookingData?.status !== "Completed" && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (setSelectedServices) {
                                    const updated = selectedServices.filter(
                                      (_, i) => i !== idx,
                                    );
                                    (setSelectedServices as any)(updated);
                                  }
                                }}
                                className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                title="Remove Service"
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {(!bookingData || status === "booked") && !rebookMode && (
                  <button
                    onClick={() => {
                      setIsAddingService(true);
                      if (setSelectionMode && selectionMode !== "Sunbeds")
                        setSelectionMode("Services");
                      setStep("selection");
                    }}
                    className="w-full py-4 border-2 border-dashed border-gray-100 rounded-xl text-gray-400 font-[600] text-xs hover:border-[#885ABB]/50 hover:bg-white transition-all flex items-center justify-center gap-2 bg-transparent"
                  >
                    <Plus size={14} />
                    {selectionMode === "Sunbeds"
                      ? "Add another sunbed"
                      : "Add another service"}
                  </button>
                )}

                {/* ── Products ── */}
                {selectedProducts.length > 0 && (
                  <div className="space-y-2 pt-1">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-1">
                      Products
                    </p>
                    {selectedProducts.map((product) => {
                      const effectivePrice =
                        product.discountedPrice ?? product.price;
                      return (
                        <div
                          key={product.id}
                          className="bg-white rounded-xl p-3 border border-gray-100 flex items-center gap-3 shadow-sm"
                        >
                          <div className="w-8 h-8 rounded-lg bg-gray-50 border border-gray-100 flex-shrink-0 overflow-hidden">
                            {product.image ? (
                              <img
                                src={product.image}
                                alt={product.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = "none";
                                }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <ShoppingBag size={12} className="text-gray-300" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-[600] text-[#402B69] truncate">
                              {product.name}
                            </p>
                            <p className="text-[11px] text-gray-400 font-medium">
                              {product.quantity} × {currencySymbol}{Number(effectivePrice).toFixed(2)}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-[#885ABB]">
                              {currencySymbol}{(Number(effectivePrice) * product.quantity).toFixed(2)}
                            </span>
                            {onRemoveProduct && (
                              <button
                                type="button"
                                onClick={() => onRemoveProduct(product.id)}
                                className="w-6 h-6 rounded-lg flex items-center justify-center text-gray-300 hover:text-red-400 hover:bg-red-50 transition-all"
                              >
                                <Trash2 size={12} />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {(!bookingData || status === "booked") && !rebookMode && onAddProducts && (
                  <button
                    onClick={onAddProducts}
                    className="w-full py-4 border-2 border-dashed border-gray-100 rounded-xl text-gray-400 font-[600] text-xs hover:border-[#885ABB]/50 hover:bg-white transition-all flex items-center justify-center gap-2 bg-transparent"
                  >
                    <ShoppingBag size={14} />
                    Add products
                  </button>
                )}
              </section>
            </div>
          </div>

          {/* FOOTER - NOW INSIDE RIGHT PANEL */}
          <div className="bg-white shadow-[0_-10px_30px_rgba(0,0,0,0.03)] border-t border-gray-100 flex flex-col w-full z-10 sticky bottom-0 lg:static">
            {/* Order Summary Section */}
            <div className="px-4 sm:px-6 pt-4 pb-2">
              <section className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-sm space-y-3">
                <div className="space-y-2">
                  <div className="flex justify-between text-xs text-gray-500 font-medium">
                    <span>Subtotal</span>
                    <span>
                      {currencySymbol}
                      {subtotal.toFixed(2)}
                    </span>
                  </div>
                  {totalDiscount > 0 && (
                    <div className="flex justify-between text-xs text-red-500 font-medium">
                      <span>Discount</span>
                      <span>
                        -{currencySymbol}
                        {totalDiscount.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="flex flex-col gap-1 py-1">
                    {(() => {
                      const taxTotals: Record<
                        string,
                        { amount: number; percent: number }
                      > = {};
                      let calculatedTotalTax = 0;

                      selectedServices.forEach((s) => {
                        const sPrice = calculateServicePrice(s);

                        // 1. Try using embedded tax objects
                        const embeddedTaxes =
                          s.service_taxes || s.tax_services || (s as any).taxes;
                        if (
                          embeddedTaxes &&
                          Array.isArray(embeddedTaxes) &&
                          embeddedTaxes.length > 0
                        ) {
                          embeddedTaxes.forEach((item: any) => {
                            const taxObj = item.tax || item;
                            if (
                              taxObj &&
                              (taxObj.percent !== undefined ||
                                taxObj.percent !== null ||
                                (taxObj as any).percent_percent !== undefined)
                            ) {
                              const percent = Number(
                                taxObj.percent ||
                                (taxObj as any).percent_percent ||
                                0,
                              );
                              const tAmount = (sPrice * percent) / 100;
                              const tName = taxObj.name || "Tax";
                              if (!taxTotals[tName]) {
                                taxTotals[tName] = {
                                  amount: 0,
                                  percent: percent,
                                };
                              }
                              taxTotals[tName].amount += tAmount;
                              calculatedTotalTax += tAmount;
                            }
                          });
                          return; // Skip fallback if embedded taxes found
                        }

                        let applicableTaxIds: number[] = [];
                        if (s.tax_ids && Array.isArray(s.tax_ids)) {
                          applicableTaxIds = s.tax_ids.map((id) => Number(id));
                        } else if (
                          (s as any).taxes &&
                          Array.isArray((s as any).taxes)
                        ) {
                          applicableTaxIds = (s as any).taxes.map((id: any) =>
                            Number(id),
                          );
                        }

                        if (applicableTaxIds.length > 0 && allTaxes) {
                          applicableTaxIds.forEach((tid) => {
                            const taxDef = allTaxes.find(
                              (t) => Number(t.id) === Number(tid),
                            );
                            if (taxDef) {
                              const tAmount = (sPrice * taxDef.percent) / 100;
                              if (!taxTotals[taxDef.name]) {
                                taxTotals[taxDef.name] = {
                                  amount: 0,
                                  percent: taxDef.percent,
                                };
                              }
                              taxTotals[taxDef.name].amount += tAmount;
                              calculatedTotalTax += tAmount;
                            }
                          });
                        }
                      });

                      // Final fallback: check for top-level booking taxes if totals are zero
                      const fallbackTaxAmount = Number(
                        (bookingData as any)?.tax_amount ||
                        (bookingData as any)?.total_tax ||
                        0,
                      );

                      if (
                        Object.keys(taxTotals).length === 0 &&
                        fallbackTaxAmount > 0
                      ) {
                        const bTaxAmt = fallbackTaxAmount;
                        const bTaxName = (bookingData as any).tax_name || "Tax";
                        const bTaxPct = Number(
                          (bookingData as any).tax_percent || 0,
                        );
                        taxTotals[bTaxName] = {
                          amount: bTaxAmt,
                          percent: bTaxPct,
                        };
                        calculatedTotalTax = bTaxAmt;
                      }

                      const totalTaxAmount = Object.values(taxTotals).reduce(
                        (sum, d) => sum + d.amount,
                        0,
                      );

                      if (totalTaxAmount === 0) {
                        return (
                          <div className="flex justify-between text-xs text-gray-400">
                            <span>Taxes</span>
                            <span>{currencySymbol}0.00</span>
                          </div>
                        );
                      }

                      return (
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>Taxes</span>
                          <span>
                            {currencySymbol}
                            {totalTaxAmount.toFixed(2)}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="pt-3 border-t border-gray-50 flex justify-between items-center">
                    <span className="text-sm sm:text-base font-[600] text-[#402B69]">
                      {bookingData?.status === "completed"
                        ? "Total Paid"
                        : "Total Due"}
                    </span>
                    <span className="text-lg sm:text-xl font-bold text-[#885ABB]">
                      {currencySymbol}
                      {(totalPrice + totalTax).toFixed(2)}
                    </span>
                  </div>
                </div>
              </section>
            </div>

            {/* Action Buttons */}
            <div className="p-4 sm:p-6 pt-2 pb-8 sm:pb-6 flex flex-col sm:flex-row gap-2 sm:gap-3">
              {bookingData &&
                !rebookMode &&
                bookingData.status !== "completed" && (
                  <button
                    onClick={handleSave}
                    disabled={loading || selectedServices.length === 0}
                    className="w-full sm:flex-1 h-12 bg-white border border-gray-100 text-[#402B69] rounded-xl font-bold hover:bg-gray-50 transition-all text-sm shadow-sm"
                  >
                    Update
                  </button>
                )}
              {(!bookingData || rebookMode) && (
                <button
                  onClick={async () => {
                    if (onSaveAsBooked) {
                      setLoading(true);
                      try {
                        await onSaveAsBooked();
                      } finally {
                        setLoading(false);
                      }
                    }
                  }}
                  disabled={loading || selectedServices.length === 0}
                  className="sm:flex-1 h-12 bg-white border border-[#885ABB] text-[#885ABB] rounded-xl font-bold hover:bg-[#885ABB]/5 transition-all text-[11px] sm:text-sm"
                >
                  Save
                </button>
              )}
              {status !== "completed" && (
                <button
                  onClick={() => setStep("payment")}
                  disabled={loading || selectedServices.length === 0}
                  className="w-full sm:flex-[1.5] h-12 bg-[#885ABB] text-white rounded-xl font-bold hover:bg-[#7748A4] shadow-lg shadow-[#885ABB]/10 transition-all flex items-center justify-center gap-2 group text-sm"
                >
                  <CreditCard size={16} />
                  <span>
                    {bookingData &&
                    Number(bookingData.amount_paid ?? 0) > 0 &&
                    Number(
                      bookingData.pending_amount ??
                        Math.max(
                          0,
                          Number(bookingData.amount_to_pay ?? 0) -
                            Number(bookingData.amount_paid ?? 0),
                        ),
                    ) > 0
                      ? "Collect Payment"
                      : "Pay Now"}
                  </span>
                  <ChevronRight
                    size={16}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </div>

      {loading && (
        <div className="absolute inset-0 z-[100] flex items-center justify-center bg-white/40 backdrop-blur-[2px]">
          <GlobalLoader
            initialPageLoad={false}
            description="Processing..."
            compensateSidebar={false}
          />
        </div>
      )}

      <DepositLinkPopup
        isOpen={showDepositPopup}
        link={depositLink}
        amount={depositAmount}
        currency={depositCurrency}
        onClose={() => setShowDepositPopup(false)}
      />
    </div>
  );
};

export default SummaryStep;
